package br.com.vvrefeicoes.portal.core.util.spreadsheet

internal class SpreadsheetFormatException(
    message: String,
) : Exception(message)

/**
 * Descompressor DEFLATE (RFC 1951) puro em Kotlin — usado para ler o conteúdo do `.xlsx`
 * (que é um ZIP). Sem dependências externas; funciona no Js e no WasmJs.
 *
 * Port do clássico "puff"/canonical Huffman decode.
 */
internal object Inflate {
    private const val MAX_BITS = 15

    private val LENGTH_BASE =
        intArrayOf(
            3,
            4,
            5,
            6,
            7,
            8,
            9,
            10,
            11,
            13,
            15,
            17,
            19,
            23,
            27,
            31,
            35,
            43,
            51,
            59,
            67,
            83,
            99,
            115,
            131,
            163,
            195,
            227,
            258,
        )
    private val LENGTH_EXTRA =
        intArrayOf(
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            0,
            1,
            1,
            1,
            1,
            2,
            2,
            2,
            2,
            3,
            3,
            3,
            3,
            4,
            4,
            4,
            4,
            5,
            5,
            5,
            5,
            0,
        )
    private val DISTANCE_BASE =
        intArrayOf(
            1,
            2,
            3,
            4,
            5,
            7,
            9,
            13,
            17,
            25,
            33,
            49,
            65,
            97,
            129,
            193,
            257,
            385,
            513,
            769,
            1025,
            1537,
            2049,
            3073,
            4097,
            6145,
            8193,
            12289,
            16385,
            24577,
        )
    private val DISTANCE_EXTRA =
        intArrayOf(
            0,
            0,
            0,
            0,
            1,
            1,
            2,
            2,
            3,
            3,
            4,
            4,
            5,
            5,
            6,
            6,
            7,
            7,
            8,
            8,
            9,
            9,
            10,
            10,
            11,
            11,
            12,
            12,
            13,
            13,
        )
    private val CODE_LENGTH_ORDER = intArrayOf(16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15)

    private val FIXED_LITERAL by lazy { buildFixedLiteral() }
    private val FIXED_DISTANCE by lazy { Huffman.build(IntArray(30) { 5 }, 30) }

    fun inflate(source: ByteArray): ByteArray {
        val reader = BitReader(source)
        val output = ByteOutput()
        while (true) {
            val last = reader.readBit()
            when (reader.readBits(2)) {
                0 -> inflateStored(reader, output)
                1 -> inflateBlock(reader, output, FIXED_LITERAL, FIXED_DISTANCE)
                2 -> inflateDynamic(reader, output)
                else -> throw SpreadsheetFormatException("Tipo de bloco DEFLATE inválido")
            }
            if (last == 1) break
        }
        return output.toByteArray()
    }

    private fun inflateStored(
        reader: BitReader,
        output: ByteOutput,
    ) {
        reader.alignToByte()
        val len = reader.readByte() or (reader.readByte() shl 8)
        val nlen = reader.readByte() or (reader.readByte() shl 8)
        if (len != (nlen.inv() and 0xFFFF)) throw SpreadsheetFormatException("Bloco DEFLATE armazenado inválido")
        repeat(len) { output.add(reader.readByte().toByte()) }
    }

    private fun inflateBlock(
        reader: BitReader,
        output: ByteOutput,
        literal: Huffman,
        distance: Huffman,
    ) {
        while (true) {
            val symbol = reader.decodeSymbol(literal)
            when {
                symbol < 256 -> output.add(symbol.toByte())
                symbol == 256 -> return
                else -> {
                    val lengthIndex = symbol - 257
                    if (lengthIndex !in LENGTH_BASE.indices) throw SpreadsheetFormatException("Símbolo de comprimento inválido")
                    val length = LENGTH_BASE[lengthIndex] + reader.readBits(LENGTH_EXTRA[lengthIndex])
                    val distanceSymbol = reader.decodeSymbol(distance)
                    if (distanceSymbol !in DISTANCE_BASE.indices) throw SpreadsheetFormatException("Símbolo de distância inválido")
                    val dist = DISTANCE_BASE[distanceSymbol] + reader.readBits(DISTANCE_EXTRA[distanceSymbol])
                    if (dist > output.size) throw SpreadsheetFormatException("Distância DEFLATE fora do intervalo")
                    repeat(length) { output.add(output[output.size - dist]) }
                }
            }
        }
    }

    private fun inflateDynamic(
        reader: BitReader,
        output: ByteOutput,
    ) {
        val hlit = reader.readBits(5) + 257
        val hdist = reader.readBits(5) + 1
        val hclen = reader.readBits(4) + 4
        val codeLengths = IntArray(19)
        for (i in 0 until hclen) codeLengths[CODE_LENGTH_ORDER[i]] = reader.readBits(3)
        val codeLengthTree = Huffman.build(codeLengths, 19)
        val lengths = IntArray(hlit + hdist)
        var index = 0
        while (index < lengths.size) {
            when (val symbol = reader.decodeSymbol(codeLengthTree)) {
                in 0..15 -> lengths[index++] = symbol
                16 -> {
                    if (index == 0) throw SpreadsheetFormatException("Repetição de código inválida")
                    val previous = lengths[index - 1]
                    repeat(3 + reader.readBits(2)) { lengths[index++] = previous }
                }
                17 -> repeat(3 + reader.readBits(3)) { lengths[index++] = 0 }
                18 -> repeat(11 + reader.readBits(7)) { lengths[index++] = 0 }
                else -> throw SpreadsheetFormatException("Símbolo de code-length inválido")
            }
        }
        val literal = Huffman.build(lengths.copyOfRange(0, hlit), hlit)
        val distance = Huffman.build(lengths.copyOfRange(hlit, hlit + hdist), hdist)
        inflateBlock(reader, output, literal, distance)
    }

    private fun buildFixedLiteral(): Huffman {
        val lengths = IntArray(288)
        for (i in 0..143) lengths[i] = 8
        for (i in 144..255) lengths[i] = 9
        for (i in 256..279) lengths[i] = 7
        for (i in 280..287) lengths[i] = 8
        return Huffman.build(lengths, 288)
    }

    private class Huffman(
        val counts: IntArray,
        val symbols: IntArray,
    ) {
        companion object {
            fun build(
                lengths: IntArray,
                count: Int,
            ): Huffman {
                val counts = IntArray(MAX_BITS + 1)
                for (i in 0 until count) counts[lengths[i]]++
                counts[0] = 0
                val offsets = IntArray(MAX_BITS + 2)
                var sum = 0
                for (bits in 1..MAX_BITS) {
                    offsets[bits] = sum
                    sum += counts[bits]
                }
                val symbols = IntArray(sum)
                for (symbol in 0 until count) {
                    if (lengths[symbol] != 0) symbols[offsets[lengths[symbol]]++] = symbol
                }
                return Huffman(counts, symbols)
            }
        }
    }

    private class BitReader(
        private val data: ByteArray,
    ) {
        private var position = 0
        private var bitBuffer = 0
        private var bitCount = 0

        fun readBit(): Int {
            if (bitCount == 0) {
                if (position >= data.size) throw SpreadsheetFormatException("Fim inesperado do fluxo DEFLATE")
                bitBuffer = data[position++].toInt() and 0xFF
                bitCount = 8
            }
            val bit = bitBuffer and 1
            bitBuffer = bitBuffer shr 1
            bitCount--
            return bit
        }

        fun readBits(count: Int): Int {
            var value = 0
            for (i in 0 until count) value = value or (readBit() shl i)
            return value
        }

        fun alignToByte() {
            bitCount = 0
        }

        fun readByte(): Int {
            if (bitCount != 0) throw SpreadsheetFormatException("Leitura de byte desalinhada")
            if (position >= data.size) throw SpreadsheetFormatException("Fim inesperado do fluxo DEFLATE")
            return data[position++].toInt() and 0xFF
        }

        fun decodeSymbol(huffman: Huffman): Int {
            var code = 0
            var first = 0
            var index = 0
            for (length in 1..MAX_BITS) {
                code = code or readBit()
                val count = huffman.counts[length]
                if (code - count < first) return huffman.symbols[index + (code - first)]
                index += count
                first += count
                first = first shl 1
                code = code shl 1
            }
            throw SpreadsheetFormatException("Código Huffman inválido")
        }
    }

    private class ByteOutput {
        private var buffer = ByteArray(1024)
        var size = 0
            private set

        fun add(value: Byte) {
            if (size == buffer.size) buffer = buffer.copyOf(buffer.size * 2)
            buffer[size++] = value
        }

        operator fun get(index: Int): Byte = buffer[index]

        fun toByteArray(): ByteArray = buffer.copyOf(size)
    }
}
